import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, ChevronDown } from 'lucide-react';

/**
 * Multiselect input matching the Activity > Events PropertyMultiselectInput.
 *
 * - Trigger button shows "{N} selected" or placeholder.
 * - Click → portal-mounted dropdown anchored to the trigger.
 * - Top: search filters options by label/value (case-insensitive).
 * - Body: scrollable checkbox list. Click a row → toggle immediately, no
 *   Apply, dropdown stays open. Outside-click / Esc closes.
 *
 * `value` is always `string[]`. `options` is `Array<{ value, label }>`
 * (label optional; falls back to value).
 */
export default function PropertyMultiselectInput({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  className = '',
  onSearchChange,
  serverSearch = false,
  searchLoading = false,
  allowCustomValue = false,
  loadingMore = false,
  footerNote = null,
  /** Called with true/false as the dropdown opens/closes: lets the owner fetch lazily. */
  onOpenChange,
  /** More options exist on the server: scrolling to the end (or the button) asks for them. */
  hasMore = false,
  onLoadMore,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef(null);
  const popRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 200 });

  const selected = Array.isArray(value) ? value : (value == null || value === '' ? [] : [value]);

  // Position the popup just below the trigger.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const update = () => {
      const t = triggerRef.current?.getBoundingClientRect();
      if (!t) return;
      const margin = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const width = Math.max(200, t.width);
      let left = t.left;
      if (left + width + margin > vw) left = Math.max(margin, vw - width - margin);
      const wantedTop = t.bottom + 4;
      const flipTop = t.top - 240 - 4;
      const top = (wantedTop + 240 + margin > vh && flipTop > margin) ? flipTop : wantedTop;
      setPos({ top: Math.max(margin, top), left: Math.max(margin, left), width });
    };
    update();
    const raf = requestAnimationFrame(update);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  useEffect(() => { onOpenChangeRef.current?.(open); }, [open]);

  const closeDropdown = () => {
    setOpen(false);
    setSearch('');
    if (onSearchChange) onSearchChange('');
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (popRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      closeDropdown();
    };
    // Escape closes this list, not the dialog it sits in.
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); closeDropdown(); } };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (val) => {
    const set = new Set(selected);
    if (set.has(val)) set.delete(val);
    else set.add(val);
    onChange(Array.from(set));
  };

  const q = search.trim().toLowerCase();
  const filtered = serverSearch
    ? (options || [])
    : (options || []).filter((o) => {
      if (!q) return true;
      const lbl = (o.label ?? o.value ?? '').toString().toLowerCase();
      const v = (o.value ?? '').toString().toLowerCase();
      return lbl.includes(q) || v.includes(q);
    });

  const showCustomRow = allowCustomValue && search.trim() && !searchLoading && filtered.length === 0;

  const triggerLabel = selected.length === 0
    ? placeholder
    : selected.length === 1
      // The option's label when it has one (a step's name, not its id); the value otherwise, as before.
      ? String((Array.isArray(options) && options.find((o) => o && o.value === selected[0])?.label) || selected[0])
      : `${selected.length} selected`;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-chip-control
        className={`inline-flex h-8 w-full items-center justify-between gap-2 rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[12px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover focus:outline-none ${className}`}
        style={{ minWidth: 140, fontFamily: 'Inter, sans-serif' }}
      >
        <span className={selected.length === 0 ? 'truncate text-[color:var(--text-muted)]' : 'truncate'} style={{ flex: 1, minWidth: 0 }}>{triggerLabel}</span>
        <ChevronDown
          size={13}
          className="flex-shrink-0 text-[color:var(--text-muted)] transition-transform"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>
      {open ? createPortal(
        <div
          ref={popRef}
          className="fixed z-[10000] flex flex-col overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface shadow-token-lg"
          style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: 280, fontFamily: 'Inter, sans-serif' }}
        >
          <div className="flex items-center gap-1.5 border-b border-[color:var(--border-default)] px-3 py-2">
            <Search size={13} className="text-[color:var(--text-muted)]" />
            <input
              autoFocus
              value={search}
              onChange={(e) => {
                const next = e.target.value;
                setSearch(e.target.value);
                if (onSearchChange) onSearchChange(next);
              }}
              placeholder="Search…"
              className="flex-1 bg-transparent text-[12px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none"
            />
          </div>
          <div
            className="flex-1 overflow-y-auto"
            style={{ padding: '4px 0' }}
            onScroll={(e) => {
              const el = e.currentTarget;
              if (hasMore && !loadingMore && !searchLoading && onLoadMore
                && el.scrollHeight - el.scrollTop - el.clientHeight < 40) onLoadMore();
            }}
          >
            {searchLoading ? (
              <div className="px-3 py-3 text-center text-[11px] text-[color:var(--text-muted)]">Searching…</div>
            ) : null}
            {!searchLoading && showCustomRow ? (
              <button
                type="button"
                onClick={() => {
                  toggle(search.trim());
                  setSearch('');
                  if (onSearchChange) onSearchChange('');
                }}
                className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-[12px] text-[color:var(--brand-primary)] transition-colors hover:bg-surface-hover"
              >
                Use &ldquo;{search.trim()}&rdquo;
              </button>
            ) : null}
            {!searchLoading && filtered.length === 0 && !showCustomRow ? (
              <div className="px-3 py-3 text-center text-[11px] text-[color:var(--text-muted)]">No matches.</div>
            ) : null}
            {!searchLoading ? filtered.map((o) => {
              const v = o.value;
              const checked = selected.includes(v);
              return (
                <label
                  key={String(v)}
                  className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[12px] transition-colors hover:bg-surface-hover ${checked ? 'bg-surface-hover' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(v)}
                    className="h-3.5 w-3.5 cursor-pointer accent-[color:var(--brand-primary)]"
                  />
                  <span className="truncate text-[color:var(--text-primary)]">{o.label ?? v}</span>
                </label>
              );
            }) : null}

            {/* Values stream in page by page, so the list is live while the
                rest arrives. Sits under the rows rather than replacing them —
                a spinner over an already-usable list is what the old blocking
                fetch effectively did. */}
            {loadingMore ? (
              <div className="flex items-center gap-1.5 px-3 py-2 text-[11px] text-[color:var(--text-muted)]">
                <span
                  className="inline-block h-2.5 w-2.5 animate-spin rounded-full border border-[color:var(--border-default)] border-t-[color:var(--text-muted)]"
                  aria-hidden
                />
                Loading more…
              </div>
            ) : null}
            {hasMore && !loadingMore && !searchLoading && onLoadMore ? (
              <button
                type="button"
                onClick={onLoadMore}
                className="w-full px-3 py-1.5 text-left text-[11px] text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
              >
                Load more
              </button>
            ) : null}
          </div>

          {footerNote ? (
            <div className="border-t border-[color:var(--border-default)] px-3 py-1.5 text-[10px] leading-snug text-[color:var(--text-muted)]">
              {footerNote}
            </div>
          ) : null}
        </div>,
        document.body,
      ) : null}
    </>
  );
}
